import { Injectable } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { IJobDeployment } from '../interfaces/job-deployment';
import { IJobFilter } from '../interfaces/job/job-filter';
import { EditService } from './edit.service';
import { HttpResponsesHandlersFactory } from '../handlers/http-responses-handler';
import { SelectItem } from 'primeng';
import { AllocateJobDTO } from '../interfaces/allocate-job';
import { UpdateTaskScheduleDTO } from '../components/task-supplier-schedule/task-supplier-schedule';
import { ScheduleUnavailabilitySummaryDTO } from '../interfaces/supplier-availability/schedule-unavailability-summary.dto.interface';
import { ISupplierCard } from '../interfaces/supplier-card';
import { SupplierCard } from '../interfaces/supplier-card-dto';
import { IDeploySupplierContext } from '../interfaces/deploy-supplier-context';

@Injectable({ providedIn: 'root' })
export class JobsDeploymentService extends EditService<IJobDeployment, IJobFilter> {
  url = '/api/job-deployment';
  entityTitle = 'Job Deployment';

  constructor(http: HttpClient, handlersFactory: HttpResponsesHandlersFactory) {
    super(http, handlersFactory);
  }

  public getDeploySupplierContext(jobId: number, supplierId: number) {
    return this.http
      .get<IDeploySupplierContext>(`${this.url}/getDeploySupplierContext/${jobId}/${supplierId}`)
      .toPromise();
  }

  public getJobInterest(jobId: number): Promise<SupplierCard[]> {
    return this.handleRequest(
      this.http.get(`${this.url}/getInterest/${jobId}`),
      this.handlersFactory.getForReadAll(),
    ).then(this.extractData);
  }

  public getJobHistory(jobId: number): Promise<any[]> {
    return this.handleRequest(
      this.http.get(`${this.url}/getHistory/${jobId}`),
      this.handlersFactory.getForReadAll(),
    ).then(this.extractData);
  }

  public allocateJob(item: AllocateJobDTO): Promise<any> {
    return this.handleRequest(
      this.http.post(this.url + '/allocateJob', item),
      this.handlersFactory.getForUpdate(this.entityTitle),
    ).then(this.extractData);
  }

  public getDriverPlan(item: any): Promise<any> {
    return this.handleRequest(
      this.http.post(`${this.url}/getDriverPlan`, item),
      this.handlersFactory.getForReadAll(),
    ).then(this.extractData);
  }

  public searchSuppliers(request: any): Promise<any> {
    return this.handleRequest(
      this.http.post(`${this.url}/searchSuppliers`, request),
      this.handlersFactory.getForReadAll(),
    ).then(this.extractData);
  }

  public getSupplierCard(jobId: number, supplierId: number): Promise<ISupplierCard> {
    return this.handleRequest(
      this.http.get(`${this.url}/getSupplierCard/${jobId}/${supplierId}`),
      this.handlersFactory.getForReadAll(),
    ).then(this.extractData);
  }

  public getSuppliersForJob(jobId: number): Promise<SelectItem[]> {
    return this.handleRequest(
      this.http.get(`${this.url}/getSuppliersForJob/${jobId}`),
      this.handlersFactory.getForReadAll(),
    ).then(this.extractData);
  }

  public checkSupplierCapability(supplierId: number, jobId: number): Promise<any> {
    return this.handleRequest(
      this.http.get(`${this.url}/checkSupplierCapability/${supplierId}/${jobId}`),
      this.handlersFactory.getForReadAll(),
    ).then(this.extractData);
  }

  public isSupplierAvailableForJob(supplierId: number, jobId: number): Promise<boolean> {
    return this.handleRequest(
      this.http.get(`${this.url}/is-supplier-available/${supplierId}/${jobId}`),
      this.handlersFactory.getForReadAll(),
    ).then(this.extractData);
  }

  checkScheduleAvailability(
    supplierId: number,
    schedules: UpdateTaskScheduleDTO[],
  ): Promise<ScheduleUnavailabilitySummaryDTO> {
    return this.http
      .post<ScheduleUnavailabilitySummaryDTO>(`${this.url}/${supplierId}/check-schedule`, schedules)
      .toPromise();
  }

  changeDriver(dto: {
    taskId: number;
    driverId: number;
    reasonId: number;
    reasonComment: string;
  }): Promise<boolean> {
    return this.http.post<boolean>(`${this.url}/changeDriver`, dto).toPromise();
  }
}
